import { useEffect, useRef, useState } from 'react'
import { OwnerInfoCard } from './OwnerInfoCard'
import { CalendarAssignmentCard } from './CalendarAssignmentCard'
import { MetricsGrid } from './MetricsGrid'
import { MfpAssignmentCard } from './MfpAssignmentCard'
import { Textarea } from '@/components/ui/textarea'
import { Loader2 } from 'lucide-react'
import { usePlanShares } from '@/hooks/usePlanShares'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useUpdatePlanHeader } from '@/hooks/useAssortmentPlans'
import { useDebounce } from '@/hooks/useDebounce'
import type { ApiPlanDetailResponse } from '@/types'

interface PlanInfoTabProps {
  planId: string
  planMetadata?: ApiPlanDetailResponse
  onTransferClick: () => void
}

export function PlanInfoTab({
  planId,
  planMetadata,
  onTransferClick,
}: PlanInfoTabProps) {
  const { data: sharesData, isLoading: sharesLoading } = usePlanShares(planId)
  const { userId: currentUserId } = useCurrentUser()
  const updateHeader = useUpdatePlanHeader(planId)

  const [notes, setNotes] = useState(planMetadata?.notes ?? '')
  const debouncedNotes = useDebounce(notes, 600)
  const isInitialMount = useRef(true)
  const serverNotes = useRef(planMetadata?.notes ?? '')

  // Sync local state when server data loads/changes
  useEffect(() => {
    const incoming = planMetadata?.notes ?? ''
    serverNotes.current = incoming
    setNotes(incoming)
    isInitialMount.current = true
  }, [planMetadata?.notes])

  // Auto-save when debounced value changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (debouncedNotes === serverNotes.current) return
    if (planMetadata?.is_frozen) return
    serverNotes.current = debouncedNotes
    updateHeader.mutate({ notes: debouncedNotes })
  }, [debouncedNotes, updateHeader, planMetadata?.is_frozen])

  const isLoading = sharesLoading || !planMetadata
  const isFrozen = planMetadata?.is_frozen ?? false

  // Determine if current user can transfer (only owner can transfer)
  const canTransfer =
    !!currentUserId &&
    !!sharesData?.owner?.id &&
    currentUserId === sharesData.owner.id

  // Get dates from plan metadata
  const createdAt = planMetadata?.created_at ?? ''
  const modifiedAt = planMetadata?.modified_at ?? ''

  const [savingFields, setSavingFields] = useState<Set<string>>(new Set())

  const handleFieldSave = (fieldKey: string, value: string | number | null) => {
    if (isFrozen) return
    setSavingFields(prev => new Set(prev).add(fieldKey))
    updateHeader.mutate(
      { [fieldKey]: value },
      {
        onSettled: () => {
          setSavingFields(prev => {
            const next = new Set(prev)
            next.delete(fieldKey)
            return next
          })
        },
      }
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse rounded-[14px] border border-[#f3f4f6] bg-[#f9fafb] p-4">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="size-[36px] rounded-[10px] bg-gray-200" />
              <div className="space-y-2">
                <div className="h-4 w-12 rounded bg-gray-200" />
                <div className="h-5 w-24 rounded bg-gray-200" />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <OwnerInfoCard
        owner={sharesData?.owner ?? null}
        createdAt={createdAt}
        modifiedAt={modifiedAt}
        canTransfer={canTransfer}
        onTransferClick={onTransferClick}
      />

      <CalendarAssignmentCard planId={planId} readOnly={isFrozen} />
      <CalendarAssignmentCard planId={planId} isSeasonal readOnly={isFrozen} />

      {planMetadata && (
        <MetricsGrid
          plan={planMetadata}
          onSave={handleFieldSave}
          savingFields={savingFields}
          readOnly={isFrozen}
        />
      )}

      <MfpAssignmentCard planId={planId} readOnly={isFrozen} />

      {/* Notes section */}
      <div className="space-y-2">
        <label className="text-[16px] font-medium text-[#262626]">Notes</label>
        <div className="relative">
          <Textarea
            placeholder="Type your notes here"
            className="h-[80px] resize-none"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            disabled={isFrozen}
          />
          {(notes !== debouncedNotes || updateHeader.isPending) && (
            <Loader2 className="absolute bottom-2 right-2 h-3.5 w-3.5 animate-spin text-gray-400" />
          )}
        </div>
      </div>
    </div>
  )
}
